import { useState, type CSSProperties, type FC, type FormEvent } from 'react';
import type { ClientCalendarEvent } from '../types';

interface ClientCalendarOverlayProps {
  events: ClientCalendarEvent[];
  isOpen: boolean;
  onClose: () => void;
  onCreateEvent: (payload: Partial<ClientCalendarEvent>) => Promise<void>;
  onToggleComplete: (id: string, isCompleted: boolean) => Promise<void>;
  onDeleteEvent: (id: string) => Promise<void>;
}

function eventTypeLabel(type: string): string {
  if (type === 'DEADLINE') return 'Дедлайн';
  if (type === 'MEETING') return 'Консультация';
  if (type === 'CONFERENCE') return 'Заседание';
  return type;
}

function eventTypeAccent(type: string): string {
  if (type === 'DEADLINE') return '#ff7b65';
  return '#41d2ff';
}

/** Client calendar — cab-overlay chrome + cabinet-* body. */
export const ClientCalendarOverlay: FC<ClientCalendarOverlayProps> = ({
  events,
  isOpen,
  onClose,
  onCreateEvent,
  onToggleComplete,
  onDeleteEvent,
}) => {
  const [filter, setFilter] = useState<string>('ALL');
  const [isAdding, setIsAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState('DEADLINE');
  const [startTime, setStartTime] = useState('2026-10-15 10:00');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const filtered = events.filter((e) => {
    if (filter === 'ALL') return true;
    if (filter === 'ACTIVE') return !e.is_completed;
    if (filter === 'COMPLETED') return e.is_completed;
    return e.event_type === filter;
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      setIsSubmitting(true);
      await onCreateEvent({
        title,
        event_type: eventType,
        start_time: startTime,
        description,
        is_completed: false,
      });
      setTitle('');
      setDescription('');
      setIsAdding(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'ALL', label: 'Все события' },
    { id: 'ACTIVE', label: 'Предстоящие' },
    { id: 'DEADLINE', label: 'Дедлайны' },
    { id: 'MEETING', label: 'Консультации' },
    { id: 'COMPLETED', label: 'Выполненные' },
  ];

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true" aria-labelledby="calendar-title">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Календарь клиента</small>
              <h2 id="calendar-title">Академический календарь</h2>
              <p>Сроки сдачи, защита этапов, консультации и дедлайны</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button
              type="button"
              className="cab-btn primary"
              onClick={() => setIsAdding(!isAdding)}
            >
              + Добавить событие
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="cab-body">
          <div className="cabinet-filters" style={{ marginBottom: 14 }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`cabinet-filter${filter === tab.id ? ' active' : ''}`}
                onClick={() => setFilter(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="cabinet-note">Событий в выбранной категории нет</div>
          ) : (
            <div className="cabinet-stack">
              {filtered.map((ev) => (
                <article
                  key={ev.id}
                  className="cabinet-card"
                  style={
                    {
                      '--accent': ev.is_completed ? '#8faebb' : eventTypeAccent(ev.event_type),
                      opacity: ev.is_completed ? 0.7 : 1,
                    } as CSSProperties
                  }
                >
                  <div className="cabinet-actions">
                    <button
                      type="button"
                      className="cabinet-btn"
                      onClick={() => void onToggleComplete(ev.id, !ev.is_completed)}
                      aria-label="Отметить статус"
                    >
                      {ev.is_completed ? '✓ Выполнено' : 'Отметить'}
                    </button>
                    <span
                      className="cabinet-badge"
                      style={{ '--badge': eventTypeAccent(ev.event_type) } as CSSProperties}
                    >
                      {eventTypeLabel(ev.event_type)}
                    </span>
                    <span style={{ fontSize: 12, color: '#41d2ff', fontWeight: 700 }}>
                      {ev.start_time}
                    </span>
                    <span className="cabinet-spacer" />
                    <button
                      type="button"
                      className="cabinet-btn"
                      onClick={() => void onDeleteEvent(ev.id)}
                      aria-label="Удалить событие"
                    >
                      Удалить
                    </button>
                  </div>
                  <strong
                    style={{
                      display: 'block',
                      marginTop: 8,
                      textDecoration: ev.is_completed ? 'line-through' : undefined,
                    }}
                  >
                    {ev.title}
                  </strong>
                  {ev.description ? (
                    <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12 }}>
                      {ev.description}
                    </p>
                  ) : null}
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {isAdding ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal" style={{ maxWidth: 560 }}>
            <header className="cabinet-modal-head">
              <div>
                <h2>Новое академическое событие</h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setIsAdding(false)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </header>
            <form className="cabinet-modal-body" onSubmit={handleSubmit}>
              <div className="cabinet-stack">
                <label className="cabinet-field">
                  <span>Название</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Название дедлайна или консультации"
                  />
                </label>
                <div className="cabinet-grid2">
                  <label className="cabinet-field">
                    <span>Тип</span>
                    <select
                      className="cabinet-select"
                      value={eventType}
                      onChange={(e) => setEventType(e.target.value)}
                    >
                      <option value="DEADLINE">Дедлайн</option>
                      <option value="MEETING">Консультация</option>
                      <option value="CONFERENCE">Заседание</option>
                    </select>
                  </label>
                  <label className="cabinet-field">
                    <span>Дата и время</span>
                    <input
                      className="cabinet-input"
                      type="text"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      placeholder="2026-10-15 10:00"
                    />
                  </label>
                </div>
                <label className="cabinet-field">
                  <span>Описание</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Описание или ссылка на подключение"
                  />
                </label>
                <div className="cabinet-actions" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => setIsAdding(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="cabinet-btn primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Сохранение…' : 'Сохранить'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};
